// Task Cards — Bird & Wildlife Pack
export const WILDLIFE = [
  {
    id: "bird-etiquette",
    photo: "/taskcards/bird-etiquette.jpg",
    icon: "Bird",
    title: { en: "Bird Etiquette", es: "Etiqueta con las Aves" },
    tagline: { en: "Ethical fieldcraft — the bird's welfare comes first.", es: "Trabajo de campo etico — el bienestar del ave es lo primero." },
    tips: {
      en: [
        "Keep your distance — use a long lens instead of approaching nests.",
        "Never disturb nesting birds or bait predators near nests.",
        "Move slowly, wear muted colors, and stay quiet.",
        "Don't flush birds for a flight shot — it stresses and endangers them.",
        "Leave the habitat exactly as you found it.",
      ],
      es: [
        "Mantén la distancia — usa un lente largo en vez de acercarte a los nidos.",
        "Nunca molestes a aves anidando ni atraigas depredadores cerca de nidos.",
        "Muevete lento, viste colores apagados y guarda silencio.",
        "No espantes aves para una foto de vuelo — las estresa y pone en peligro.",
        "Deja el habitat exactamente como lo encontraste.",
      ],
    },
    remember: { en: "The bird's welfare always beats the shot.", es: "El bienestar del ave siempre vale más que la foto." },
  },
  {
    id: "birds-in-water",
    photo: "/taskcards/birds-in-water.jpg",
    icon: "Drop",
    title: { en: "Birds in Water", es: "Aves en el Agua" },
    tagline: { en: "Capture waterfowl, waders and reflections at the water's edge.", es: "Captura aves acuaticas, zancudas y reflejos en la orilla." },
    settings: [
      { k: "aperture", v: "f/5.6–f/8" },
      { k: "shutter", v: "1/1000s+" },
      { k: "focus", v: "AF-C + single point on eye" },
    ],
    tips: {
      en: [
        "Get low — at the water's level — for intimate angles and mirror reflections.",
        "Calm water at dawn gives the cleanest reflections.",
        "Watch for splashes, ripples and droplets to add action.",
        "Expose carefully: white birds blow out easily — meter for the highlights.",
        "Soft morning side light brings out feather detail and color.",
      ],
      es: [
        "Ponte bajo — al nivel del agua — para ángulos intimos y reflejos de espejo.",
        "El agua en calma al amanecer da los reflejos más limpios.",
        "Atento a salpicaduras, ondas y gotas para añadir acción.",
        "Expón con cuidado: las aves blancas se queman fácil — mide para las altas luces.",
        "La suave luz lateral matutina resalta el detalle y color de las plumas.",
      ],
    },
    remember: { en: "Get low, watch highlights, calm water reflects.", es: "Ponte bajo, cuida las altas luces, el agua en calma refleja." },
  },
  {
    id: "birds-flying",
    photo: "/taskcards/birds-flying.jpg",
    icon: "Wind",
    title: { en: "Birds in Flight", es: "Aves en Vuelo" },
    tagline: { en: "The ultimate test of tracking, shutter and patience.", es: "La prueba definitiva de seguimiento, velocidad y paciencia." },
    settings: [
      { k: "shutter", v: "1/2000s–1/4000s" },
      { k: "aperture", v: "f/5.6–f/8" },
      { k: "focus", v: "AF-C + zone/wide tracking" },
    ],
    tips: {
      en: [
        "Very fast shutter (1/2000s+) to freeze beating wings.",
        "Use continuous AF with subject tracking and burst mode.",
        "Keep both eyes open and 'pan' to follow the bird smoothly.",
        "Shoot with the sun behind you for lit, detailed feathers.",
        "Find a clean sky or distant background to keep AF locked on.",
      ],
      es: [
        "Velocidad muy alta (1/2000s+) para congelar el aleteo.",
        "Usa AF continuo con seguimiento de sujeto y modo ráfaga.",
        "Mantén ambos ojos abiertos y 'barre' para seguir al ave con suavidad.",
        "Dispara con el sol a tu espalda para plumas iluminadas y detalladas.",
        "Busca un cielo limpio o fondo lejano para mantener el AF fijo.",
      ],
    },
    remember: { en: "Fast shutter, AF-C tracking, sun behind you.", es: "Velocidad alta, seguimiento AF-C, sol a tu espalda." },
  },
  {
    id: "backyard-feeder-birds",
    photo: "/taskcards/backyard-feeder-birds.jpg",
    icon: "Bird",
    title: { en: "Backyard & Feeder Birds", es: "Aves de Jardín y Comedero" },
    tagline: { en: "Stunning bird shots without traveling — right from home.", es: "Fotos de aves asombrosas sin viajar — desde casa." },
    settings: [
      { k: "aperture", v: "f/4–f/6.3" },
      { k: "shutter", v: "1/800s+" },
      { k: "focus", v: "AF-C on a set perch" },
    ],
    tips: {
      en: [
        "Set up a natural perch near the feeder and pre-focus on it.",
        "Hide the feeder from frame; photograph birds on the pretty perch.",
        "Shoot from a window, blind, or car so birds relax.",
        "Morning light and a clean, distant background look professional.",
        "Be patient and quiet — let the birds come to you.",
      ],
      es: [
        "Coloca una percha natural junto al comedero y pre-enfoca en ella.",
        "Oculta el comedero del encuadre; fotografía a las aves en la percha bonita.",
        "Dispara desde una ventana, escondite o auto para que las aves se relajen.",
        "La luz matutina y un fondo limpio y lejano lucen profesionales.",
        "Se paciente y silencioso — deja que las aves vengan a ti.",
      ],
    },
    remember: { en: "Natural perch, hidden feeder, patient and quiet.", es: "Percha natural, comedero oculto, paciencia y silencio." },
  },
  {
    id: "animal-behavior",
    photo: "/taskcards/animal-behavior.jpg",
    icon: "PawPrint",
    title: { en: "Animal Behavior & Anticipation", es: "Comportamiento Animal y Anticipación" },
    tagline: { en: "Great wildlife shots come from reading the moment before it happens.", es: "Las grandes fotos de fauna vienen de leer el momento antes de que ocurra." },
    tips: {
      en: [
        "Learn your subject's habits — feeding, grooming, take-off signals.",
        "Watch body language: a crouch, a wing-stretch or a head-turn precedes action.",
        "Keep the camera up and pre-focused during tense moments.",
        "Use silent/electronic shutter and burst mode for peak action.",
        "Patience beats luck — the more you observe, the better you predict.",
      ],
      es: [
        "Aprende los habitos de tu sujeto — alimentación, acicalado, señales de despegue.",
        "Observa el lenguaje corporal: agacharse, estirar el ala o girar la cabeza precede la acción.",
        "Mantén la cámara lista y pre-enfocada en momentos de tensión.",
        "Usa obturador silencioso/electrónico y ráfaga para el punto máximo de acción.",
        "La paciencia supera a la suerte — cuanto más observas, mejor predices.",
      ],
    },
    remember: { en: "Anticipate the action; the best shot is a prediction.", es: "Anticipa la acción; la mejor foto es una predicción." },
  },
];
